import type { TimelineViewTransform } from "../ViewTransform/transform";
import { viewTransformCenteredOn } from "../ViewTransform/transform";

/** Fastest the locked zoom may change, in scale units per second. */
const MAX_ZOOM_PER_SEC = 0.45;

/** Fastest the locked frame may shift vertically, in world px per second. */
const MAX_CENTER_Y_PER_SEC = 240;

/** About how long the camera takes to settle on a new frame. */
const SETTLE_SEC = 0.35;

/** One frame never spends more time than this, even after a stall. */
const FIT_EASE_MAX_STEP_MS = 50;

/** How fast the camera is moving now, so it can speed up and slow down gradually. */
export interface LockedViewVelocity {
  zoom: number;
  y: number;
  /** Where it really is. The view itself is rounded to steady the grid. */
  exact?: { zoom: number; y: number };
}

/** Further than this from the exact spot, something else moved the camera. */
const STILL_EXACT_Y_PX = 4;
const STILL_EXACT_ZOOM = 0.01;

export const stillCamera = (): LockedViewVelocity => ({ zoom: 0, y: 0 });

export function fitEaseDelta(nowMs: number, previousMs: number): number {
  if (previousMs <= 0) {
    return 0;
  }
  return Math.min(FIT_EASE_MAX_STEP_MS, Math.max(0, nowMs - previousMs));
}

/**
 * Follow the playhead immediately. Ease zoom and the vertical center so a
 * marker or branch leaving does not snap the locked frame.
 */
export function easedLockedFrame(
  current: TimelineViewTransform,
  fitted: TimelineViewTransform,
  heldZoom: number | null,
  deltaMs: number,
  stageW: number,
  stageH: number,
  velocity: LockedViewVelocity = stillCamera(),
): TimelineViewTransform {
  if (heldZoom !== null) {
    velocity.zoom = 0;
    velocity.y = 0;
    velocity.exact = undefined;
    return fitted;
  }
  return smoothLockedView(current, fitted, deltaMs, stageW, stageH, velocity);
}

export function smoothLockedView(
  current: TimelineViewTransform,
  target: TimelineViewTransform,
  deltaMs: number,
  stageW: number,
  stageH: number,
  velocity: LockedViewVelocity = stillCamera(),
): TimelineViewTransform {
  const from = worldCenter(current, stageW, stageH);
  const to = worldCenter(target, stageW, stageH);
  const { exact } = velocity;
  const trustExact = exact !== undefined
    && Math.abs(exact.y - from.y) < STILL_EXACT_Y_PX
    && Math.abs(exact.zoom - current.zoom) < STILL_EXACT_ZOOM;
  const eased = trustExact ? { ...exact } : { zoom: current.zoom, y: from.y };
  for (let left = deltaMs; left > 0; left -= FIT_EASE_MAX_STEP_MS) {
    const step = Math.min(left, FIT_EASE_MAX_STEP_MS) / 1000;
    const nextZoom = glide(eased.zoom, target.zoom, velocity.zoom, step, MAX_ZOOM_PER_SEC);
    const nextY = glide(eased.y, to.y, velocity.y, step, MAX_CENTER_Y_PER_SEC);
    eased.zoom = nextZoom.value;
    velocity.zoom = nextZoom.velocity;
    eased.y = nextY.value;
    velocity.y = nextY.velocity;
  }
  velocity.exact = eased;
  const { zoom, y } = eased;
  return viewTransformCenteredOn(to.x, y, zoom, stageW, stageH);
}

function worldCenter(
  view: TimelineViewTransform,
  stageW: number,
  stageH: number,
): { x: number; y: number } {
  const zoom = Math.max(view.zoom, 0.002);
  return {
    x: (stageW / 2 - view.panX) / zoom,
    y: (stageH / 2 - view.panY) / zoom,
  };
}

/** A critically damped glide: no sudden start or stop, and it never overshoots. */
function glide(
  from: number,
  to: number,
  velocity: number,
  stepSec: number,
  maxPerSec: number,
): { value: number; velocity: number } {
  const omega = 2 / SETTLE_SEC;
  const x = omega * stepSec;
  const decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
  const reach = maxPerSec * SETTLE_SEC;
  const gap = Math.max(-reach, Math.min(reach, from - to));
  const aim = from - gap;
  const carried = (velocity + omega * gap) * stepSec;
  const value = aim + (gap + carried) * decay;
  const nextVelocity = (velocity - omega * carried) * decay;
  const overshot = (from >= to && value < to) || (from <= to && value > to);
  return overshot ? { value: to, velocity: 0 } : { value, velocity: nextVelocity };
}
